import React from 'react';
import {
  Autocomplete,
  Checkbox,
  Collapse,
  FormControlLabel,
  Stack,
  TextField
} from '@mui/material';
import { styles } from '../../styles';
import { SingleDatePicker } from '../../../../Components/CustomUIElements/SingleDatePicker';
import { sharedClasses } from '../../../../Components/CustomUIElements/sharedClasses';
import { IntelliHRState, IntelliHRAction } from './intelliHRReducer';
import { Dispatch } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { IIntelliHrData, ICustomField } from '../../../types';
import { FormTextField } from '../../../../Components/CustomUIElements/FormTextField';
import moment from 'moment';

interface CreateJobStepProps {
  state: IntelliHRState;
  dispatch: Dispatch<IntelliHRAction>;
  fieldRefs: {
    jobStartDate: React.RefObject<HTMLInputElement>;
    probationEndDate: React.RefObject<HTMLInputElement>;
    positionTitle: React.RefObject<HTMLInputElement>;
    businessEntity: React.RefObject<HTMLInputElement>;
    businessUnit: React.RefObject<HTMLInputElement>;
    location: React.RefObject<HTMLInputElement>;
    supervisor: React.RefObject<HTMLInputElement>;
    employmentCondition: React.RefObject<HTMLInputElement>;
    payGrade: React.RefObject<HTMLInputElement>;
    workClass: React.RefObject<HTMLInputElement>;
    fte: React.RefObject<HTMLInputElement>;
  };
  renderCustomFields: (field: ICustomField, type: 'person' | 'job') => React.ReactNode;
}

export default function CreateJobStep({
  state,
  dispatch,
  fieldRefs,
  renderCustomFields
}: CreateJobStepProps) {
  const queryClient = useQueryClient();
  const intelliHrData = queryClient.getQueryData<IIntelliHrData>(['intelliHr data']);
  const queryState = queryClient.getQueryState(['intelliHr data']);
  const loadingIntelliHrData = queryState?.fetchStatus === 'fetching';

  return (
    <>
      <Stack sx={styles.cognologySectionTitle}>Create Job</Stack>
      <Stack sx={styles.modalDoubleColumn}>
        <Stack sx={{ width: '48%' }}>
          <SingleDatePicker
            value={state.jobStartDate}
            label="Job start date"
            inputStyles={{
              ...sharedClasses.datePicker,
              width: '100%',
              height: '34px'
            }}
            onChange={(value) => {
              if (!value?.isValid()) {
                dispatch({
                  type: 'SET_COMPANY_START_DATE_ERROR',
                  payload: 'Please input a valid date'
                });
              } else {
                dispatch({ type: 'SET_COMPANY_START_DATE_ERROR', payload: '' });
                dispatch({
                  type: 'SET_COMPANY_START_DATE',
                  payload: value.toDate()
                });
              }
            }}
            required
            error={state.jobStartDateError || state.errors.jobStartDate}
            innerRef={fieldRefs.jobStartDate}
          />
        </Stack>
        <Stack sx={{ width: '48%' }}>
          <SingleDatePicker
            value={state.jobEndDate}
            label="Job end date"
            inputStyles={{
              ...sharedClasses.datePicker,
              width: '100%',
              height: '34px'
            }}
            onChange={(value) => {
              if (!value?.isValid()) {
                dispatch({
                  type: 'SET_COMPANY_END_DATE_ERROR',
                  payload: 'Please input a valid date'
                });
              } else {
                dispatch({ type: 'SET_COMPANY_END_DATE_ERROR', payload: '' });
                dispatch({
                  type: 'SET_COMPANY_END_DATE',
                  payload: value.toDate()
                });
              }
            }}
            error={state.jobEndDateError}
            disabled={state.hasEndDate}
          />
          <FormControlLabel
            control={
              <Checkbox
                sx={{ ...sharedClasses.formCheckbox, padding: 'unset', marginRight: 1 }}
                checked={state.hasEndDate}
                onChange={(event) =>
                  dispatch({ type: 'SET_HAS_END_DATE', payload: event.target.checked })
                }
              />
            }
            label="This job doesn't have an end date"
            sx={{ ...sharedClasses.checkboxLabel, margin: 'unset', marginTop: '4px' }}
          />
        </Stack>
      </Stack>
      <Stack sx={{ rowGap: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              sx={{ ...sharedClasses.formCheckbox, padding: 'unset', marginRight: 1 }}
              checked={state.probationIsNotApplicable}
              onChange={(event) =>
                dispatch({ type: 'SET_PROBATION_IS_NOT_APPLICABLE', payload: event.target.checked })
              }
            />
          }
          label="Probation is not applicable"
          sx={{ marginLeft: 'unset' }}
        />
        <Collapse in={!state.probationIsNotApplicable}>
          <Stack sx={styles.modalDoubleColumn}>
            <Stack sx={{ width: '48%' }}>
              <SingleDatePicker
                value={state.probationEndDate}
                label="Probation end date"
                inputStyles={{
                  ...sharedClasses.datePicker,
                  width: '100%',
                  height: '34px'
                }}
                onChange={(value) => {
                  if (!value?.isValid()) {
                    dispatch({
                      type: 'SET_PROBATION_END_DATE_ERROR',
                      payload: 'Please input a valid date'
                    });
                  } else {
                    dispatch({ type: 'SET_PROBATION_END_DATE_ERROR', payload: '' });
                    dispatch({
                      type: 'SET_PROBATION_END_DATE',
                      payload: value.toDate()
                    });
                  }
                }}
                required
                error={state.probationEndDateError || state.errors.probationEndDate}
                disabled={state.useDefaultProbationLength}
                innerRef={fieldRefs.probationEndDate}
                minDate={moment(state.jobStartDate).add(7, 'days')} // we need to use moment because the component is using AdapterMoment
              />
            </Stack>
            <FormControlLabel
              control={
                <Checkbox
                  sx={{ ...sharedClasses.formCheckbox, padding: 'unset', marginRight: 1 }}
                  checked={state.useDefaultProbationLength}
                  onChange={(event) =>
                    dispatch({
                      type: 'SET_USE_DEFAULT_PROBATION_LENGTH',
                      payload: event.target.checked
                    })
                  }
                />
              }
              label="Use default probation length of 6 months"
              sx={{ ...sharedClasses.checkboxLabel, margin: 'unset', marginTop: 2 }}
            />
          </Stack>
        </Collapse>
      </Stack>
      <Stack sx={styles.modalDoubleColumn}>
        <Autocomplete
          disablePortal
          freeSolo
          autoSelect
          options={intelliHrData?.intellihr_data.position_titles.map((title) => title.name) || []}
          value={state.positionTitle}
          loading={loadingIntelliHrData}
          sx={{ ...sharedClasses.formAutocomplete, width: '48%', marginTop: 3 }}
          slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Position title"
              placeholder="Please select"
              slotProps={{ inputLabel: { shrink: true } }}
              required
              helperText={state.errors.positionTitle}
              ref={fieldRefs.positionTitle}
              sx={styles.autoCompleteTextfield}
            />
          )}
          onChange={(event, value) => dispatch({ type: 'SET_POSITION_TITLE', payload: value })}
        />
        <Autocomplete
          disablePortal
          options={intelliHrData?.intellihr_data.locations || []}
          getOptionLabel={(option) => option.name}
          value={state.location}
          loading={loadingIntelliHrData}
          sx={{ ...sharedClasses.formAutocomplete, width: '48%', marginTop: 3 }}
          slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Location"
              placeholder="Please select"
              slotProps={{ inputLabel: { shrink: true } }}
              required
              helperText={state.errors.location}
              ref={fieldRefs.location}
              sx={styles.autoCompleteTextfield}
            />
          )}
          onChange={(event, value) => dispatch({ type: 'SET_LOCATION', payload: value })}
        />
      </Stack>
      <Stack sx={styles.modalDoubleColumn}>
        <Autocomplete
          disablePortal
          options={intelliHrData?.intellihr_data.business_entities || []}
          getOptionLabel={(option) => option.name}
          value={state.businessEntity}
          loading={loadingIntelliHrData}
          sx={{ ...sharedClasses.formAutocomplete, width: '48%', marginTop: 3 }}
          slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Business entity"
              placeholder="Please select"
              slotProps={{ inputLabel: { shrink: true } }}
              required
              helperText={state.errors.businessEntity}
              ref={fieldRefs.businessEntity}
              sx={styles.autoCompleteTextfield}
            />
          )}
          onChange={(event, value) => dispatch({ type: 'SET_BUSINESS_ENTITY', payload: value })}
        />
        <Autocomplete
          disablePortal
          options={intelliHrData?.intellihr_data.business_units || []}
          getOptionLabel={(option) => option.name}
          value={state.businessUnit}
          loading={loadingIntelliHrData}
          sx={{ ...sharedClasses.formAutocomplete, width: '48%', marginTop: 3 }}
          slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Business unit"
              placeholder="Please select"
              slotProps={{ inputLabel: { shrink: true } }}
              required
              helperText={state.errors.businessUnit}
              ref={fieldRefs.businessUnit}
              sx={styles.autoCompleteTextfield}
            />
          )}
          onChange={(event, value) => dispatch({ type: 'SET_BUSINESS_UNIT', payload: value })}
        />
      </Stack>
      <Stack sx={styles.cognologySectionTitle}>Reporting</Stack>
      <Stack sx={styles.modalDoubleColumn}>
        <Autocomplete
          disablePortal
          options={intelliHrData?.intellihr_data.managers || []}
          getOptionLabel={(option) => option.name}
          value={state.supervisor}
          loading={loadingIntelliHrData}
          sx={{ ...sharedClasses.formAutocomplete, width: '48%', marginTop: 3 }}
          slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Supervisor"
              placeholder="Please select"
              slotProps={{ inputLabel: { shrink: true } }}
              required
              helperText={state.errors.supervisor}
              ref={fieldRefs.supervisor}
              sx={styles.autoCompleteTextfield}
            />
          )}
          onChange={(event, value) => dispatch({ type: 'SET_SUPERVISOR', payload: value })}
          disabled={state.noSupervisor}
        />
        <FormControlLabel
          control={
            <Checkbox
              sx={{ ...sharedClasses.formCheckbox, padding: 'unset', marginRight: 1 }}
              checked={state.noSupervisor}
              onChange={(event) =>
                dispatch({ type: 'SET_NO_SUPERVISOR', payload: event.target.checked })
              }
            />
          }
          label="This job doesn't have a supervisor"
          sx={{ ...sharedClasses.checkboxLabel, margin: 'unset', marginTop: '28px' }}
        />
      </Stack>
      <Stack sx={styles.cognologySectionTitle}>Position arrangements</Stack>
      <Stack sx={styles.modalDoubleColumn}>
        <Autocomplete
          disablePortal
          options={intelliHrData?.intellihr_data.employment_conditions || []}
          getOptionLabel={(option) => option.name}
          value={state.employmentCondition}
          loading={loadingIntelliHrData}
          sx={{ ...sharedClasses.formAutocomplete, width: '48%', marginTop: 3 }}
          slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Employment condition"
              placeholder="Please select"
              slotProps={{ inputLabel: { shrink: true } }}
              required
              helperText={state.errors.employmentCondition}
              ref={fieldRefs.employmentCondition}
              sx={styles.autoCompleteTextfield}
            />
          )}
          onChange={(event, value) =>
            dispatch({ type: 'SET_EMPLOYMENT_CONDITION', payload: value })
          }
        />
        <Autocomplete
          disablePortal
          options={intelliHrData?.intellihr_data.pay_grades || []}
          getOptionLabel={(option) => option.name}
          value={state.payGrade}
          loading={loadingIntelliHrData}
          sx={{ ...sharedClasses.formAutocomplete, width: '48%', marginTop: 3 }}
          slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Pay grade"
              placeholder="Please select"
              slotProps={{ inputLabel: { shrink: true } }}
              required
              helperText={state.errors.payGrade}
              ref={fieldRefs.payGrade}
              sx={styles.autoCompleteTextfield}
            />
          )}
          onChange={(event, value) => dispatch({ type: 'SET_PAY_GRADE', payload: value })}
        />
      </Stack>
      <Stack sx={styles.modalDoubleColumn}>
        <Autocomplete
          disablePortal
          options={intelliHrData?.intellihr_data.work_classes || []}
          getOptionLabel={(option) => option.name}
          value={state.workClass}
          loading={loadingIntelliHrData}
          sx={{ ...sharedClasses.formAutocomplete, width: '48%', marginTop: 3 }}
          slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Work class"
              placeholder="Please select"
              slotProps={{ inputLabel: { shrink: true } }}
              required
              helperText={state.errors.workClass}
              ref={fieldRefs.workClass}
              sx={styles.autoCompleteTextfield}
            />
          )}
          onChange={(event, value) => dispatch({ type: 'SET_WORK_CLASS', payload: value })}
        />
      </Stack>
      <Stack sx={styles.modalDoubleColumn}>
        <FormTextField
          label="FTE"
          value={state.fte}
          required
          fullWidth
          onChange={(e) => dispatch({ type: 'SET_FTE', payload: e.target.value })}
          draggable={state.noFTE}
          styles={{ '.Mui-disabled': { background: 'grey', opacity: 0.2 } }}
          error={state.errors.fte}
          innerRef={fieldRefs.fte}
        />
        <FormControlLabel
          control={
            <Checkbox
              sx={{ ...sharedClasses.formCheckbox, padding: 'unset', marginRight: 1 }}
              checked={state.noFTE}
              onChange={(event) => dispatch({ type: 'SET_NO_FTE', payload: event.target.checked })}
            />
          }
          label="This job doesn't have an FTE"
          sx={{ ...sharedClasses.checkboxLabel, margin: 'unset', marginTop: '20px', width: '100%' }}
        />
      </Stack>
      {intelliHrData?.intellihr_data.job_custom_fields.length && (
        <>
          <Stack sx={styles.cognologySectionTitle}>Custom fields</Stack>
          <Stack sx={styles.customFieldsContainer}>
            {intelliHrData?.intellihr_data.job_custom_fields.map((field) =>
              renderCustomFields(field, 'job')
            )}
          </Stack>
        </>
      )}
    </>
  );
}
